/**
 * What a desk viewer may do, as one set both doors derive from the same
 * tables (WEBSITE-FORMS-PLATFORM §16). The server enforces every capability
 * again (enforceDeskAccess); this set only decides what the desk offers.
 */
export const DESK_CAPABILITIES = [
    /** See the queue and open a referral. */
    'referrals.view',
    /** Move a referral along its stages (advance, lapse, reopen). */
    'referrals.move',
    /** Verify a website referral by linking it to a member. */
    'referrals.verify',
    /** Invite a website referrer to join the program. */
    'referrals.inviteReferrer',
    /** Reject a referral. */
    'referrals.reject',
    /** Keep one of two duplicate referrals (desk_decides). */
    'referrals.resolveDuplicate',
    /** Read the fee cards. */
    'fees.view',
    /** Edit the fee cards and move referrers between them. */
    'fees.edit',
    /** Read payouts, gift card requests and the float. */
    'payouts.view',
    /** Mark payouts paid, issue or cancel gift cards, record float top-ups, send client rewards. */
    'payouts.manage',
    /** The members screen: invite, edit, suspend, resend, cancel. */
    'members.manage',
    /** Read applications to join. */
    'applications.view',
    /** Approve or decline applications. */
    'applications.decide',
    /** Read the program settings. */
    'settings.view',
    /** Change the program settings. */
    'settings.edit',
    /** Tenant desk only: link a referral to its CRM deal, and unlink it. */
    'crm.link',
    /** Tenant desk only: map CRM stages to referral stages. */
    'crm.stageMap',
];
const KNOWN = new Set(DESK_CAPABILITIES);
/** A set from any list; unknown strings are dropped, so a newer server value cannot widen the desk. */
export function capabilitySet(list) {
    const out = new Set();
    for (const c of list)
        if (KNOWN.has(c))
            out.add(c);
    return out;
}
const CRM = ['crm.link', 'crm.stageMap'];
/** Everything except the CRM capabilities: the most a partner-portal account can hold. */
const PORTAL_ADMIN = DESK_CAPABILITIES.filter((c) => !CRM.includes(c));
/**
 * Partner staff: works the queue and reads everything else, as the portal
 * has always offered them. An administrator verifies, invites, edits fees,
 * pays out, manages members, decides applications and changes settings.
 */
const PORTAL_STAFF = [
    'referrals.view',
    'referrals.move',
    'referrals.reject',
    'referrals.resolveDuplicate',
    'fees.view',
    'payouts.view',
    'applications.view',
    'settings.view',
];
/** Standalone partner portal (apps/loyalty). Never includes a CRM capability. */
export function capabilitiesForPartnerRole(role) {
    if (role === 'partner_admin')
        return capabilitySet(PORTAL_ADMIN);
    if (role === 'partner_staff')
        return capabilitySet(PORTAL_STAFF);
    return capabilitySet([]);
}
/**
 * Tenant-operated program inside the VALUTTEN client app (§16 table):
 * super_admin and administrator hold the full desk including CRM linking and
 * the stage map; marketing verifies, invites, rejects, resolves duplicates and
 * moves stages, with no fees, payouts, settings or CRM. Every other tenant
 * role has no desk.
 */
export function capabilitiesForTenantRole(role) {
    if (role === 'super_admin' || role === 'administrator')
        return capabilitySet(DESK_CAPABILITIES);
    if (role === 'marketing') {
        return capabilitySet([
            'referrals.view',
            'referrals.move',
            'referrals.verify',
            'referrals.inviteReferrer',
            'referrals.reject',
            'referrals.resolveDuplicate',
        ]);
    }
    return capabilitySet([]);
}
